import { Form, Modal, Tabs } from 'antd'
import UploadingComponent from 'components/UploadingComponent/UploadingComponent';
import { IDocument, IFolder } from 'interfaces';
import React from 'react'
import classes from './ShareModal.module.scss';
import { useTranslation } from 'react-i18next';
import { setIsOpenShareEntityModal } from 'store/slices/windowStateSlice';
import { useAppDispatch, useAppSelector } from 'store/hook';
import dayjs from 'dayjs';
import api from 'api';
import ShareForm from './ShareForm';
import ShareManagementTable from './ShareManagementTable';
import { ExpireTypes } from './types';
import cn from 'classnames';
import { getTheme } from 'helpers/getTheme';

type ShareModalProps = {
  open: boolean;
  onCancel: () => void;
  entity: IDocument | IFolder;
};

export default function ShareModal({ open, onCancel, entity }: ShareModalProps) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [expires, setExpires] = React.useState<ExpireTypes>(ExpireTypes.Count);
  const [isPasswordEnabled, setIsPasswordEnabled] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<'create' | 'management'>('create');
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const emailValue = Form.useWatch('email', form);
  // const roomId = useAppSelector((state) => state.dataRoom?.dataRoom?.id);

  const { messageApi } = useAppSelector((state) => state.windowState);


  const handleCancel = () => {
    setIsPasswordEnabled(false);
    setActiveTab('create');
    onCancel();
  };
  const getExpiresValue = (values: any) => {
    switch(expires) {
      case ExpireTypes.Time:
        return `${dayjs(values[expires]).utc().format('YYYY-MM-DD HH:mm')}:59`;
      default: 
        return values[expires];
    };
  }
  const onFinish = async (values: any) => {
    const expires_value = getExpiresValue(values);
const isNoEmailAndUserName = !values.email && !values.user_name;


    const payload = { 
      ...(!isNoEmailAndUserName ? {
        addressees: [
          {
            user_name: values.user_name,
            email: values.email
          }
        ]
      }:{}),
      mode: values.mode,
      expires_option: values.expires_option,
      document_token: entity.token, 
      expires_value,
      ...(isPasswordEnabled && values.password ? { password: values.password } : {})
    };

    setIsLoading(true);
    try {
      const response = await api.shareDocument(payload);
      if (isNoEmailAndUserName) {
        await navigator.clipboard.writeText(response.data.message);
        messageApi?.success(t('Documents.modals.share.linkCopied'), 10);
      } else {
        messageApi?.success(`${t('Documents.modals.share.sharedSuccess')} ${values.email}`, 10);
      }
    } catch (e) {
      messageApi?.error(`${t('Documents.modals.share.sharedError')} ${entity.name}`, 5);
    } finally {
      form.resetFields();
      setIsPasswordEnabled(false);
      setActiveTab('create');
      setIsLoading(false);
      dispatch(setIsOpenShareEntityModal(false));
    }
  };

  const tabItems = [
    {
      key: 'create',
      label: t('Documents.modals.share.tabs.create'),
      children: (
        <ShareForm
          form={form}
          onFinish={onFinish}
          expires={expires}
          setExpires={setExpires}
          isPasswordEnabled={isPasswordEnabled}
          setIsPasswordEnabled={setIsPasswordEnabled}
        />
      ),
    },
    {
      key: 'management',
      label: t('Documents.modals.share.tabs.management'),
      children: <ShareManagementTable token={entity.token} />,
    },
  ];

  return (
    <Modal
      centered
      open={open}
      onCancel={handleCancel}
      cancelText={t('main.cancelButton')}
      title={`${t('Documents.modals.share.shareTitle')}: ${entity.name}`}
      okText={activeTab === 'create'
        ? (!!emailValue && form.getFieldError('email').length === 0
          ? t('Documents.modals.share.sendLink')
          : t('Documents.modals.share.createLink'))
        : undefined
      }
      onOk={activeTab === 'create' ? form.submit : undefined}
      okButtonProps={{disabled: isLoading, style: { display: activeTab === 'create' ? 'inline-block' : 'none' }}}
      cancelButtonProps={{disabled: isLoading}}
      className={cn(classes.shareModal, getTheme())}
      width={800}
    >
      <Tabs
        activeKey={activeTab}
        onChange={(key: string) => setActiveTab(key as 'create' | 'management')}
        items={tabItems}
      />
      {isLoading &&
        <UploadingComponent />
      }
    </Modal>
  )
}
